/** @jsxImportSource @emotion/react */
import {
  Badge,
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
} from '@mui/material'
import { format } from 'date-fns'
import { useAtomValue, useSetAtom } from 'jotai'
import { useMemo, useState } from 'react'

import { UserCustomRole, UserProfile, UserRole } from '@app/types'
import { userToRemoveAtom } from '../state/adminAtom'
import { clientNameAtom } from '@app/state/clientsAtom'
import { useDeleteAdminUser } from '../hooks/useDeleteAdminUser'
import { useUpdateUserRoles } from '../hooks/useUpdateUserRoles'
import { canResendInvite, STATUS_ORDER, handleRolesCascade } from '../utils/Admin.utils'
import { SELECTABLE_ROLES, ROLE_COLORS, USER_ROLES_LABELS_MAP } from '@app/app-constants'

import { Icon } from '@app/components/Atoms/Icon'
import { Autocomplete } from '@app/components/Atoms/Autocomplete/Autocomplete'
import { roleListItem, rolesList } from '../styles/Admin.styles'

type Order = 'asc' | 'desc'

type AdminUsersTableProps = {
  users: UserProfile[]
  resendingEmails?: Set<string>
  onResendInvite?: (email: string) => void
  customRoles: UserCustomRole[] | undefined
  onDeleteUser?: (user: UserProfile) => void
}

const AdminUsersTable = ({
  users,
  onResendInvite,
  resendingEmails,
  customRoles,
}: AdminUsersTableProps) => {
  const clientName = useAtomValue(clientNameAtom)
  const setUserToRemove = useSetAtom(userToRemoveAtom)

  const deleteUserMutation = useDeleteAdminUser()
  const updateUserRolesMutation = useUpdateUserRoles()

  const [order, setOrder] = useState<Order>('asc')
  const [orderBy, setOrderBy] = useState<
    'logged_in_count' | 'last_logged_in' | 'status' | 'email' | 'date_created' | ''
  >('')
  const [editingUser, setEditingUser] = useState<string | null>(null)
  const [rolesForEdit, setRolesForEdit] = useState<UserRole[]>([UserRole.MEMBER, UserRole.VIEWER])
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(0)

  const rowsPerPage = 10

  const handleSort = (
    property: 'logged_in_count' | 'last_logged_in' | 'status' | 'email' | 'date_created',
  ) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const filteredUsers = useMemo(() => {
    const query = searchQuery.toLowerCase().trim()
    if (!query) return users
    return users.filter((user) => user.email?.toLowerCase().includes(query))
  }, [users, searchQuery])

  const sortedUsers = useMemo(() => {
    if (!orderBy) return filteredUsers

    return [...filteredUsers].sort((a, b) => {
      let aVal: number | string | Date | undefined | null = a[orderBy]
      let bVal: number | string | Date | undefined | null = b[orderBy]

      if (aVal == null) return 1
      if (bVal == null) return -1

      if (orderBy === 'last_logged_in') {
        aVal = new Date(aVal).getTime()
        bVal = new Date(bVal).getTime()
      }

      if (orderBy === 'status') {
        aVal = STATUS_ORDER[String(aVal).toUpperCase()] ?? 0
        bVal = STATUS_ORDER[String(bVal).toUpperCase()] ?? 0
      }

      if (orderBy === 'email') {
        aVal = String(aVal).toLowerCase()
        bVal = String(bVal).toLowerCase()
      }

      if (aVal < bVal) return order === 'asc' ? -1 : 1
      if (aVal > bVal) return order === 'asc' ? 1 : -1
      return 0
    })
  }, [filteredUsers, order, orderBy])

  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage
    const end = start + rowsPerPage
    return sortedUsers.slice(start, end)
  }, [sortedUsers, page, rowsPerPage])

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage)

  return (
    <>
      <TableContainer>
        <Box sx={{ display: 'flex', p: 1.5 }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by email..."
            aria-label="Search users by email"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(0)
            }}
            sx={{ pl: 1, mb: 4, width: '50%' }}
          />
        </Box>

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'email' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'email'}
                  direction={orderBy === 'email' ? order : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell align="right" sortDirection={orderBy === 'date_created' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'date_created'}
                  direction={orderBy === 'date_created' ? order : 'asc'}
                  onClick={() => handleSort('date_created')}
                ></TableSortLabel>
                Signed Up
              </TableCell>

              <TableCell
                align="right"
                sortDirection={orderBy === 'logged_in_count' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'logged_in_count'}
                  direction={orderBy === 'logged_in_count' ? order : 'asc'}
                  onClick={() => handleSort('logged_in_count')}
                >
                  Login Count
                </TableSortLabel>
              </TableCell>

              <TableCell align="right" sortDirection={orderBy === 'last_logged_in' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'last_logged_in'}
                  direction={orderBy === 'last_logged_in' ? order : 'asc'}
                  onClick={() => handleSort('last_logged_in')}
                >
                  Last Login
                </TableSortLabel>
              </TableCell>

              <TableCell align="center" sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>

              <TableCell align="center" sortDirection={orderBy === 'status' ? order : false}>
                Roles
              </TableCell>
              <TableCell align="center">Delete</TableCell>
              <TableCell align="center">Edit</TableCell>
              <TableCell align="center">Resend</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedUsers.map((user) => {
              const isResending = resendingEmails?.has(user.email) ?? false

              return (
                <TableRow key={user.user_uuid}>
                  <TableCell>{user.email}</TableCell>

                  <TableCell align="right">
                    {user?.date_created ? format(user.date_created, 'P H:mm aa') : '-'}
                  </TableCell>

                  <TableCell align="right">{user.logged_in_count ?? '-'}</TableCell>

                  <TableCell align="right">
                    {user.last_logged_in ? format(user.last_logged_in, 'P H:mm aa') : '-'}
                  </TableCell>

                  <TableCell align="center">
                    {(user.status === 'UNCONFIRMED' || !user.status) && (
                      <Badge color="info" badgeContent="pending" />
                    )}
                    {user.status === 'CONFIRMED' && (
                      <Badge color="success" badgeContent="confirmed" />
                    )}
                  </TableCell>
                  <TableCell align="center" style={{ minWidth: '340px' }}>
                    {user.email === editingUser ? (
                      <Autocomplete
                        value={rolesForEdit}
                        options={[
                          ...SELECTABLE_ROLES,
                          ...(customRoles
                            ? customRoles.map((role) => role.name.toLowerCase() as UserRole)
                            : []),
                        ]}
                        fixedOptions={['viewer']}
                        onChange={(_, value) =>
                          setRolesForEdit(handleRolesCascade(value) as UserRole[])
                        }
                        labelsMap={{
                          ...USER_ROLES_LABELS_MAP,
                          ...(customRoles
                            ? Object.fromEntries(
                                customRoles.map((role) => [
                                  role.name.toLowerCase(),
                                  role.description,
                                ]),
                              )
                            : {}),
                        }}
                        small
                      />
                    ) : (
                      <List css={rolesList}>
                        {user.user_roles.sort().map((role: UserRole) => {
                          // Filter out non-assignable roles (STAFF)
                          if (role === UserRole.STAFF) return null

                          // Type guard: at this point role can only be VIEWER, MEMBER, or ADMIN
                          const displayableRole = role as
                            | UserRole.VIEWER
                            | UserRole.MEMBER
                            | UserRole.ADMIN

                          return (
                            <ListItem
                              title={USER_ROLES_LABELS_MAP[displayableRole]}
                              css={roleListItem}
                              key={`${user.user_uuid}-${role}`}
                            >
                              <Badge
                                color={ROLE_COLORS[displayableRole] || 'info'}
                                badgeContent={role}
                              />
                            </ListItem>
                          )
                        })}
                      </List>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => setUserToRemove(user)}
                      disabled={deleteUserMutation.isPending}
                    >
                      <Icon iconName="Delete" />
                    </IconButton>
                  </TableCell>
                  <TableCell align="center" style={{ width: '120px' }}>
                    {editingUser === user.email ? (
                      <>
                        <IconButton
                          onClick={() => {
                            updateUserRolesMutation.mutate({
                              uuid: user.user_uuid,
                              clientName: clientName!,
                              roles: rolesForEdit,
                            })
                            setEditingUser(null)
                          }}
                          disabled={updateUserRolesMutation.isPending}
                        >
                          <Icon iconName="SaveIcon" />
                        </IconButton>
                        <IconButton
                          onClick={() => {
                            setEditingUser(null)
                          }}
                        >
                          <Icon iconName="Close" />
                        </IconButton>
                      </>
                    ) : (
                      <IconButton
                        onClick={() => {
                          setEditingUser(user.email)
                          setRolesForEdit(user.user_roles)
                        }}
                        disabled={updateUserRolesMutation.isPending}
                      >
                        <Icon iconName="EditIcon" />
                      </IconButton>
                    )}
                  </TableCell>

                  <TableCell align="center">
                    {canResendInvite(user.status) && (
                      <IconButton
                        onClick={() => onResendInvite?.(user.email)}
                        disabled={!onResendInvite || isResending}
                        size="small"
                      >
                        {isResending ? <CircularProgress size={18} /> : <Icon iconName="Refresh" />}
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}

            {sortedUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {searchQuery ? `No users found matching "${searchQuery}"` : 'No users to display'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={sortedUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPageOptions={[10]}
        />
      </TableContainer>
    </>
  )
}

export default AdminUsersTable
